<ng-template #structuralDetailsModal>
    <form [formGroup]="form" (ngSubmit)="save()" novalidate>
        <div class="modal-header flex-column align-items-start">
            <div class="d-flex flex-wrap gap-2 mb-2">
                <span class="badge bg-light text-dark">Plant Name: {{ plant?.name || '—' }}</span>
                <span class="badge bg-light text-dark" *ngIf="plant?.region">Region: {{ plant.region }}</span>
                <span class="badge bg-light text-dark" *ngIf="plant?.city">City: {{ plant.city }}</span>
            </div>
            <h4 class="modal-title">Structural Field Details</h4>
        </div>

        <div class="modal-body form-styles">
            <div class="loaderwrapper" *ngIf="loading || saving">
                <span class="loader"></span>
            </div>

            <div class="row">
                <!-- Structure Type -->
                <div class="col-md-6 mb-3">
                    <label for="sfd-type">Structure Type <span class="text-danger">*</span></label>
                    <ng-select labelForId="sfd-type" formControlName="structure_type" [items]="structureTypes"
                        bindLabel="name" bindValue="id" placeholder="Select structure type" [clearable]="false"
                        (change)="onStructureTypeChange($event?.id)">
                    </ng-select>
                    <small class="text-danger" *ngIf="hasError('structure_type')">Structure Type is required.</small>
                </div>

                <!-- Structure Sub-Type -->
                <div class="col-md-6 mb-3">
                    <label for="sfd-subtype">Structure Sub-Type <span class="text-danger">*</span></label>
                    <ng-select labelForId="sfd-subtype" formControlName="structure_sub_type"
                        [items]="structureSubTypes" bindLabel="name" bindValue="id" [clearable]="false"
                        [loading]="subTypesLoading" loadingText="Loading sub-types..."
                        notFoundText="No sub-type for this structure type"
                        [placeholder]="form.get('structure_type').value ? 'Select sub-type' : 'Select a structure type first'">
                    </ng-select>
                    <small class="text-danger" *ngIf="hasError('structure_sub_type')">Structure Sub-Type is required.</small>
                </div>

                <!-- True South Alignment -->
                <div class="col-md-6 mb-3">
                    <label for="sfd-azimuth">True South Alignment</label>
                    <div class="d-flex align-items-center gap-2">
                        <div class="form-check mb-0">
                            <input class="form-check-input" type="checkbox" id="sfd-true-south"
                                formControlName="true_south_alignment">
                            <label class="form-check-label" for="sfd-true-south">Yes</label>
                        </div>
                        <span>°</span>
                        <input id="sfd-azimuth" type="number" class="form-control" formControlName="azimuth_angle"
                            placeholder="0-360" min="0" max="360" step="0.01"
                            [class.is-invalid]="hasError('azimuth_angle')">
                    </div>
                    <small class="text-danger" *ngIf="hasError('azimuth_angle', 'required')">Azimuth angle is required when aligned to true south.</small>
                    <small class="text-danger" *ngIf="hasError('azimuth_angle', 'min') || hasError('azimuth_angle', 'max')">Must be between 0 and 360.</small>
                    <small class="text-danger" *ngIf="hasError('azimuth_angle', 'decimalPlaces')">Up to 2 decimal places.</small>
                </div>

                <!-- Structure Angle / Tilt -->
                <div class="col-md-6 mb-3">
                    <label for="sfd-tilt">Structure Angle / Tilt</label>
                    <div class="d-flex align-items-center gap-2">
                        <input id="sfd-tilt" type="number" class="form-control" formControlName="structure_angle_tilt"
                            placeholder="0-90" min="0" max="90" step="0.01"
                            [class.is-invalid]="hasError('structure_angle_tilt')">
                        <span>°</span>
                    </div>
                    <small class="text-danger" *ngIf="hasError('structure_angle_tilt', 'min') || hasError('structure_angle_tilt', 'max')">Must be between 0 and 90.</small>
                    <small class="text-danger" *ngIf="hasError('structure_angle_tilt', 'decimalPlaces')">Up to 2 decimal places.</small>
                </div>

                <ng-container *ngTemplateOutlet="measure; context: { name: 'row_spacing_pitch_distance', id: 'sfd-row', label: 'Row Spacing / Pitch Distance', unit: 'Meters' }"></ng-container>
                <ng-container *ngTemplateOutlet="measure; context: { name: 'ground_clearance_leg_height', id: 'sfd-ground', label: 'Ground Clearance / Leg Height', unit: 'Meters' }"></ng-container>
                <ng-container *ngTemplateOutlet="measure; context: { name: 'wind_load_design_rating', id: 'sfd-wind', label: 'Wind Load Design Rating', unit: 'km/h' }"></ng-container>
                <ng-container *ngTemplateOutlet="measure; context: { name: 'structure_to_inverter_distance', id: 'sfd-inverter', label: 'Structure-to-Inverter Distance', unit: 'Meters' }"></ng-container>

                <!-- Material Composition -->
                <div class="col-md-6 mb-3">
                    <label for="sfd-material">Material Composition</label>
                    <ng-select labelForId="sfd-material" formControlName="material_composition" [items]="materials"
                        bindLabel="name" bindValue="id" placeholder="Select material">
                    </ng-select>
                </div>

                <ng-container *ngTemplateOutlet="measure; context: { name: 'anti_corrosion_coating_thickness', id: 'sfd-coating', label: 'Anti-Corrosion Coating Thickness', unit: 'Microns' }"></ng-container>

                <!-- Waterproofing Certification -->
                <div class="col-md-6 mb-3">
                    <label for="sfd-cert-date">Waterproofing Certification</label>
                    <div class="d-flex align-items-center gap-2">
                        <div class="form-check form-switch mb-0">
                            <input class="form-check-input" type="checkbox" id="sfd-certified"
                                formControlName="waterproofing_certified">
                            <label class="form-check-label" for="sfd-certified">Certified</label>
                        </div>
                        <input id="sfd-cert-date" type="text" class="form-control" bsDatepicker
                            [bsConfig]="datepickerConfig" [maxDate]="today" placeholder="mm/dd/yyyy"
                            formControlName="waterproofing_certification_date" readonly
                            [class.is-invalid]="hasError('waterproofing_certification_date')">
                    </div>
                    <small class="text-danger" *ngIf="hasError('waterproofing_certification_date', 'required')">Certification date is required when certified.</small>
                    <small class="text-danger" *ngIf="hasError('waterproofing_certification_date', 'futureDate')">Date cannot be in the future.</small>
                </div>
            </div>

            <!-- Number input with a unit suffix; shares the min/decimal messages. -->
            <ng-template #measure let-name="name" let-id="id" let-label="label" let-unit="unit">
                <div class="col-md-6 mb-3">
                    <label [attr.for]="id">{{ label }}</label>
                    <div class="d-flex align-items-center gap-2">
                        <input [id]="id" type="number" class="form-control" [formControlName]="name"
                            min="0" step="0.001" [class.is-invalid]="hasError(name)">
                        <span>{{ unit }}</span>
                    </div>
                    <small class="text-danger" *ngIf="hasError(name, 'min')">Cannot be negative.</small>
                    <small class="text-danger" *ngIf="hasError(name, 'decimalPlaces')">Up to 3 decimal places.</small>
                </div>
            </ng-template>
        </div>

        <div class="modal-footer no-bg">
            <button type="button" class="btn btn-outline-dark" (click)="close()" [disabled]="saving">Close</button>
            <button type="submit" class="btn btn-dark" [disabled]="loading || saving">
                <span class="spinner-border spinner-border-sm me-2" *ngIf="saving"></span>
                Save
            </button>
        </div>
    </form>
</ng-template>
